import { Navigate, useLocation } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { WorkspacePicker } from "@/components/WorkspacePicker";

function AuthStateCard({ title, description, children, loading = false }) {
  return (
    <div className="mc-bg flex min-h-screen items-center justify-center px-4">
      <div className="glass-dark w-full max-w-md rounded-2xl p-8 text-center text-white">
        {loading ? (
          <Loader2 size={24} className="mx-auto animate-spin text-sm-accent" />
        ) : (
          <AlertTriangle size={24} className="mx-auto text-[#FBBF24]" />
        )}
        <h1 className="font-display mt-4 text-xl font-bold">{title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-white/60">{description}</p>
        {children ? <div className="mt-6">{children}</div> : null}
      </div>
    </div>
  );
}

function SignOutButton({ onClick, className = "" }) {
  return (
    <button onClick={onClick} className={`rounded-full border border-white/20 px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.09em] text-white ${className}`.trim()}>
      Sign Out
    </button>
  );
}

export const Protected = ({ children }) => {
  const location = useLocation();
  const { authStatus, authError, member, memberships, sessionEmail, selectClient, retryAuth, signOut, demoAvailable, enterDemoView } = useApp();
  if (authStatus === "loading" || authStatus === "signing-in") {
    return <AuthStateCard loading title="Opening your workspace" description="Verifying your session and client access…" />;
  }
  if (authStatus === "select-client") {
    return <WorkspacePicker memberships={memberships} selectClient={selectClient} member={member} signOut={signOut} />;
  }
  if (authStatus === "access-denied") {
    return (
      <AuthStateCard
        title="Access is not configured"
        description={sessionEmail
          ? `You are signed in as ${sessionEmail}, which is not assigned to a client workspace. Sign out and sign in with the email address your team was invited with, or contact the GrowthOS team to request access.`
          : "This account is authenticated, but it is not assigned to a client workspace. Contact the GrowthOS team to request access."}
      >
        <SignOutButton onClick={signOut} />
      </AuthStateCard>
    );
  }
  if (authStatus === "error") {
    return (
      <AuthStateCard
        title="Workspace unavailable"
        description={authError?.requestId ? `The dashboard could not open. Reference ${authError.requestId}.` : "The dashboard could not verify your workspace. Please try again."}
      >
        <button onClick={retryAuth} className="rounded-full bg-sm-primary px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.09em] text-white">
          Try Again
        </button>
        {/* The session survives a reload, so without this the card is all a refused account ever sees. */}
        <SignOutButton onClick={signOut} className="ml-3" />
        {demoAvailable ? (
          <button
            data-testid="auth-error-demo-btn"
            onClick={enterDemoView}
            className="mt-3 block w-full rounded-full border border-sm-accent/45 px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.09em] text-white"
          >
            Open Demo View
          </button>
        ) : null}
      </AuthStateCard>
    );
  }
  if (authStatus !== "authenticated") return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}${location.hash}` }} />;
  return children;
};
